import React, { useEffect, useState } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createUser, updateUser } from '../../api/userApi';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getPublishers } from '../../api/publisherApi';
import { getAgencies } from '../../api/agencyApi';
import { getAdvertisers } from '../../api/advertiserApi';
import { createOption, Option, telefoneMask } from '../../utils';
import { useProfile } from '../../hooks/UserHooks';
import { User } from '../../types/user';
import InputMask from 'react-input-mask';
import { getAdNetworks } from '../../api/adNetworkApi';

interface ModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: User | null;
    onAddData: (newData: User) => void;
    onUpdateData: (newData: User) => void;
}

const userStatusOptions = [
    {
        options: [
            createOption('Ativo', '1'),
            createOption('Inativo', '0'),
        ],
    },
];

const AddEditUserModal = ({ user, isOpen, toggle, onAddData, onUpdateData }: ModalProps) => {
    const { userProfile } = useProfile();
    const [loading, setLoading] = useState<boolean>(false);
    const [publishers, setPublishers] = useState<any>([]);
    const [selectPublisherValue, setSelectPublisherValue] = useState<SingleValue<Option>>();
    const [advertisers, setAdvertisers] = useState<any>([]);
    const [selectAdvertiserValue, setSelectAdvertiserValue] = useState<SingleValue<Option>>();
    const [agencies, setAgencies] = useState<any>([]);
    const [adNetworks, setAdNetworks] = useState<any>([]);
    const [selectAgencyValue, setSelectAgencyValue] = useState<SingleValue<Option>>();
    const [selectAdNetworkValue, setSelectAdNetworkValue] = useState<SingleValue<Option>>();
    const [userStatusValue, setUserStatusValue] = useState<Option>();

    const toggleModal = () => {
        toggle();

        if (isOpen) {
            handleForm.resetForm();
            setSelectPublisherValue(undefined);
            setSelectAdvertiserValue(undefined);
            setSelectAgencyValue(undefined);
            setSelectAdNetworkValue(undefined);
            setUserStatusValue(undefined);
        }
    };
    useEffect(() => {
        if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
            getPublishers().then(setPublishers);
            getAgencies().then(setAgencies);
            getAdvertisers().then(setAdvertisers);
            getAdNetworks().then(setAdNetworks);
        }
    }, []);

    useEffect(() => {
        if (user) {
            setUserStatusValue(createOption(user.status ? 'Ativo' : 'Inativo', user.status));
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            if (publishers && user.publisher) {
                selectPublisherOnChange(createOption(user.publisher.name, user.publisher.id));
            }

            if (agencies && user.agency) {
                selectAgencyOnChange(createOption(user.agency.name, user.agency.id));
            }

            if (adNetworks && user.adNetwork) {
                selectAdNetworkOnChange(createOption(user.adNetwork.name, user.adNetwork.id));
            }

            if (advertisers && user.advertiser) {
                selectAdvertiserOnChange(createOption(user.advertiser.name, user.advertiser.id));
            }
        }
    }, [publishers, agencies, adNetworks, advertisers, user]);

    const selectPublisherOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectPublisherValue(newValue);
        handleForm.setFieldValue('publisherId', newValue ? Number(newValue.value) : '');
    };

    const selectAgencyOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectAgencyValue(newValue);
        handleForm.setFieldValue('agencyId', newValue ? Number(newValue.value) : '');
    };

    const selectAdNetworkOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectAdNetworkValue(newValue);
        handleForm.setFieldValue('adNetworkId', newValue ? Number(newValue.value) : '');
    };

    const selectAdvertiserOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectAdvertiserValue(newValue);
        handleForm.setFieldValue('advertiserId', newValue ? Number(newValue.value) : '');
    };

    const userStatusOnChange = (newValue: any) => {
        setUserStatusValue(newValue);
        handleForm.setFieldValue('status', newValue ? Number(newValue.value) : '');

    };

    const minimalUserAge = new Date((new Date()).setFullYear((new Date()).getFullYear() - 16));

    const parseDateString = (value: string): Date | '' => {
        if (!value || value.length !== 10) return '';
        const [day, month, year] = value.split('/');
        const date = new Date(`${year}-${month}-${day}`);
        return isNaN(date.getTime()) ? '' : date;
    }

    const formatDate = (value: string) => {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
    }

    const handleForm: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: user?.id || '',
            publisherId: user?.publisher?.id || '',
            advertiserId: user?.advertiser?.id || '',
            agencyId: user?.agency?.id || '',
            adNetworkId: user?.adNetwork?.id || '',
            name: user?.name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            birthday: user?.birthday ? formatDate(user.birthday) : '',
            jobTitle: user?.jobTitle || '',
            status: user?.status || 1,
        },
        validationSchema: Yup.object({
            publisherId: Yup.number()
                .nullable()
                .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                    const {advertiserId, agencyId, adNetworkId} = this.parent;
                    const filledFields = [value, advertiserId, agencyId, adNetworkId].filter(Boolean).length;
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        return true;
                    }
                    return filledFields === 1;
                }),
            advertiserId: Yup.number()
                .nullable()
                .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                    const {publisherId, agencyId, adNetworkId} = this.parent;
                    const filledFields = [value, publisherId, agencyId, adNetworkId].filter(Boolean).length;
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        return true;
                    }
                    return filledFields === 1;
                }),
            agencyId: Yup.number()
                .nullable()
                .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                    const {publisherId, advertiserId, adNetworkId} = this.parent;
                    const filledFields = [value, publisherId, advertiserId, adNetworkId].filter(Boolean).length;
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        return true;
                    }
                    return filledFields === 1;
                }),
            adNetworkId: Yup.number()
                .nullable()
                .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser, Agência ou Ad Network', function (value) {
                    const {publisherId, advertiserId, agencyId} = this.parent;
                    const filledFields = [value, publisherId, advertiserId, agencyId].filter(Boolean).length;
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        return true;
                    }
                    return filledFields === 1;
                }),
            name: Yup.string().trim().required("Nome é obrigatório"),
            email: Yup.string().lowercase().email("Email inválido").required("Email é obrigatório"),
            phone: Yup.string().trim().nullable().transform(value => value?.replace(/\D/g, '')).min(10, "Telefone inválido").max(11, "Telefone inválido"),
            birthday: Yup.string()
                .nullable()
                .test('valid-date', 'Data inválida. Use o formato DD/MM/YYYY', function (value) {
                    if (!value) return true;
                    const parsedDate = parseDateString(value);
                    return parsedDate !== null;
                })
                .test('min-age', 'Usuário deve ter no mínimo 16 anos', function (value) {
                    if (!value) return true;
                    const parsedDate = parseDateString(value);
                    if (parsedDate === null) return false;
                    return parsedDate <= minimalUserAge;
                }),
            jobTitle: Yup.string().trim().nullable(),
            status: Yup.number().transform(value => Number(value)).required("Status é obrigatório"),
        }),
        onSubmit: (values: any) => {
            setLoading(true);

            if (!values.publisherId) {
                values.publisherId = null;
            }

            if (!values.advertiserId) {
                values.advertiserId = null;
            }

            if (!values.agencyId) {
                values.agencyId = null;
            }

            if (!values.adNetworkId) {
                values.adNetworkId = null;
            }

            if (values.birthday) {
                const [day, month, year] = values.birthday.split('/');
                values.birthday = `${year}-${month}-${day}`;
            }

            if (user) {
                updateUser(values)
                    .then((res: any) => {
                        toggleModal();
                        toast.success('Usuário editado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createUser(values)
                    .then((res: any) => {
                        toggleModal();
                        toast.success('Usuário cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });
    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleModal} className="bg-light p-3">
                    {user ? 'Editar' : 'Adicionar'} usuário
                </ModalHeader>

                <ModalBody>
                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                        <div className={'row'}>
                            <div className={'col'}>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="publisherId"
                                        className="form-label"
                                    >
                                        Publisher
                                    </Label>
                                    <Select
                                        isClearable={true}
                                        isSearchable={true}
                                        name="publisherId"
                                        options={publishers && publishers.map((publisher: any) => {
                                            return createOption(publisher.name, String(publisher.id));
                                        })}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: !!(handleForm.touched.publisherId && handleForm.errors.publisherId) ? 'red' : '#ced4da',
                                            }),
                                        }}
                                        onChange={selectPublisherOnChange}
                                        value={selectPublisherValue}
                                    />
                                    {handleForm.touched.publisherId && handleForm.errors.publisherId ? (
                                        <FormFeedback
                                            type="invalid"
                                            style={{display: 'block'}}>{handleForm.errors.publisherId}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                            <div className={'col'}>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="advertiserId"
                                        className="form-label"
                                    >
                                        Advertiser
                                    </Label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable={true}
                                        isSearchable={true}
                                        name="advertiserId"
                                        options={advertisers && advertisers.map((advertiser: any) => {
                                            return {value: advertiser.id, label: advertiser.name};
                                        })}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: !!(handleForm.touched.advertiserId && handleForm.errors.advertiserId) ? 'red' : '#ced4da',
                                            }),
                                        }}
                                        onChange={selectAdvertiserOnChange}
                                        value={selectAdvertiserValue}
                                    />
                                    {handleForm.touched.advertiserId && handleForm.errors.advertiserId ? (
                                        <FormFeedback
                                            type="invalid"
                                            style={{display: 'block'}}>{handleForm.errors.advertiserId}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                            <div className={'col'}>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="agencyId"
                                        className="form-label"
                                    >
                                        Agência
                                    </Label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable={true}
                                        isSearchable={true}
                                        name="agencyId"
                                        options={agencies && agencies.map((agency: any) => {
                                            return {value: agency.id, label: agency.name};
                                        })}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: !!(handleForm.touched.agencyId && handleForm.errors.agencyId) ? 'red' : '#ced4da',
                                            }),
                                        }}
                                        onChange={selectAgencyOnChange}
                                        value={selectAgencyValue}
                                    />
                                    {handleForm.touched.agencyId && handleForm.errors.agencyId ? (
                                        <FormFeedback
                                            type="invalid"
                                            style={{display: 'block'}}>{handleForm.errors.agencyId}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                            <div className={'col'}>
                                <div className="mb-3">
                                    <Label
                                        htmlFor="adNetworkId"
                                        className="form-label"
                                    >
                                        Ad Network
                                    </Label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable={true}
                                        isSearchable={true}
                                        name="adNetworkId"
                                        options={adNetworks && adNetworks.map((adNetwork: any) => {
                                            return {value: adNetwork.id, label: adNetwork.name};
                                        })}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: !!(handleForm.touched.adNetworkId && handleForm.errors.adNetworkId) ? 'red' : '#ced4da',
                                            }),
                                        }}
                                        onChange={selectAdNetworkOnChange}
                                        value={selectAdNetworkValue}
                                    />
                                    {handleForm.touched.adNetworkId && handleForm.errors.adNetworkId ? (
                                        <FormFeedback
                                            type="invalid"
                                            style={{display: 'block'}}>{handleForm.errors.adNetworkId}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={'row'}>
                        <div className={'col'}>
                            <div className="mb-3">
                                <Label
                                    htmlFor="name"
                                    className="form-label"
                                >
                                    Nome
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="name"
                                    name="name"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.name}
                                    invalid={
                                        !!(handleForm.touched.name && handleForm.errors.name)
                                    }
                                />
                                {handleForm.touched.name && handleForm.errors.name ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.name}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label
                                    htmlFor="email"
                                    className="form-label"
                                >
                                    Email
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="email"
                                    name="email"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.email}
                                    invalid={
                                        !!(handleForm.touched.email && handleForm.errors.email)
                                    }
                                />
                                {handleForm.touched.email && handleForm.errors.email ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.email}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label
                                    htmlFor="phone"
                                    className="form-label"
                                >
                                    Telefone
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="phone"
                                    name="phone"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={telefoneMask(handleForm.values.phone)}
                                    invalid={
                                        !!(handleForm.touched.phone && handleForm.errors.phone)
                                    }
                                />
                                {handleForm.touched.phone && handleForm.errors.phone ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.phone}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label
                                    htmlFor="birthday"
                                    className="form-label"
                                >
                                    Aniversário
                                </Label>
                                <InputMask
                                    className="form-control"
                                    id="birthday"
                                    name="birthday"
                                    onChange={handleForm.handleChange}
                                    value={handleForm.values.birthday}
                                    mask="99/99/9999"/>
                                {handleForm.touched.birthday && handleForm.errors.birthday ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.birthday}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label
                                    htmlFor="jobTitle"
                                    className="form-label"
                                >
                                    Cargo
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="jobTitle"
                                    name="jobTitle"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.jobTitle}
                                    invalid={
                                        !!(handleForm.touched.jobTitle && handleForm.errors.jobTitle)
                                    }
                                />
                                {handleForm.touched.jobTitle && handleForm.errors.jobTitle ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.jobTitle}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label
                                    htmlFor="user_status"
                                    className="form-label"
                                >
                                    Status
                                </Label>
                                <Select
                                    id="user_status"
                                    name="user_status"
                                    styles={{
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            borderColor: !!(handleForm.touched.user_status && handleForm.errors.user_status) ? 'red' : '#ced4da',
                                        }),
                                    }}
                                    defaultValue={userStatusOptions[0].options[0]}
                                    onChange={userStatusOnChange}
                                    value={userStatusValue}
                                    options={userStatusOptions}
                                />
                                {handleForm.touched.user_status && handleForm.errors.user_status ? (
                                    <FormFeedback
                                        type="invalid"
                                        style={{display: 'block'}}>{handleForm.errors.user_status}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {user ? 'Atualizar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditUserModal;